import { Container, Pressable, SxProp, View } from 'dripsy';
import { DocumentResult } from 'expo-document-picker';
import React from 'react';
import { ImagePickerResult } from 'react-native';
import { UniButton } from '../buttons';
import { ButtonVariant } from '../buttons/useButton.hook';
import { Icon, SVGIconList } from '../icons/Icon';
import { TextCaption, TextTitle } from '../typography';
import { useFileUploader } from './useFileUploader';
import { useTranslation } from 'react-i18next';

interface FileUploaderProps {
  label: string;
  uploadURL: string;
  variant?: 'squared' | 'rounded';
  onValueChange: (document: undefined | DocumentResult | ImagePickerResult) => void;
  onSuccess: (response: any) => void;
  onError: (error: any) => void;
  sx?: SxProp;
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  label,
  uploadURL,
  variant = 'squared',
  onValueChange,
  onSuccess,
  onError,
  sx
}) => {
  const { t } = useTranslation('card-account');
  const controller = useFileUploader({
    uploadURL,
    onValueChange,
    onSuccess,
    onError
  });

  const styles = {
    ...baseStyles.container,
    ...variantStyles[variant],
    borderColor: controller.status === 'ERROR' ? '$fileUploaderBorderError' : '$fileUploaderBorderDefault'
  };

  const ICON: Record<string, SVGIconList> = {
    NOT_SELECTED: 'fileUploadZeroState',
    UPLOADING: 'fileUploading',
    SUCCESS: 'fileUploadedBackground',
    ERROR: 'fileUploadedBackground'
  };

  const TITLE = {
    NOT_SELECTED: 'Drop your file here or click to upload',
    UPLOADING: 'Uploading File...',
    SUCCESS: 'File Uploaded',
    ERROR: 'Error Text'
  };

  const INSTRUCTION = {
    NOT_SELECTED:
      'Please make sure your file is clearly visible (Sharp and not blurry) and that it is less than 25 mb in size. We may request for a better image if your file lacks sufficient quality.',
    UPLOADING: 'We are now processing your document.',
    SUCCESS: t('card-account::upload-page.text--re-upload-reminder'),
    ERROR: t('card-account::upload-page.text--upload-again-by-dragging')
  };

  const COLOR = {
    NOT_SELECTED: '$secondary',
    UPLOADING: '$secondary',
    SUCCESS: '$success',
    ERROR: '$fileUploaderError'
  };

  return (
    <View>
      <TextCaption semiBold>{label}</TextCaption>
      <Pressable onPress={controller.handlePress}>
        <Container sx={{ ...styles, ...sx }}>
          <Icon name={ICON[controller.status]} height={80} width={80} defaultSVGColor />
          <View sx={{ height: '$5' }} />
          <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
            <TextTitle semiBold sx={{ color: COLOR[controller.status] }}>
              {TITLE[controller.status]}
            </TextTitle>
            {controller.status === 'SUCCESS' && <Icon name="fileUploadSuccess" height={20} width={20} />}
            {controller.status === 'ERROR' && <Icon name="fileUploadError" height={20} width={20} />}
          </View>
          <View sx={{ height: '$5' }} />
          <TextCaption secondary>{INSTRUCTION[controller.status]}</TextCaption>
          {controller.status === 'UPLOADING' && (
            <React.Fragment>
              <View sx={{ height: '$5' }} />
              <UniButton label="Cancel" onPress={() => {}} variant={ButtonVariant.outline1} sx={{ height: 36, width: 139 }} />
            </React.Fragment>
          )}
        </Container>
      </Pressable>
    </View>
  );
};

const baseStyles = {
  container: {
    paddingX: '$10',
    paddingY: '$7',
    borderWidth: 2,
    borderColor: '$fileUploaderBorderDefault',
    borderStyle: 'dashed',
    alignItems: 'center',
    justifyContent: 'center'
  }
};

const errorStyles = {
  borderColor: '$fileUploaderBorderError'
};

const variantStyles = {
  squared: {
    borderRadius: 0
  },
  rounded: {
    borderRadius: 5
  }
};
